import React, { useState } from 'react';
import { Modal, Text, Area, Select, Banner, Money, EvidenceBadge } from './ui';
import { useStore, uid } from '../state/store';
import {
  EXCEPTION_TRIGGERS,
  DECISION_TYPES,
  PRODUCT_CLASSES,
  EVIDENCE_LEVELS,
  ACTIVITY,
} from '../lib/constants';
import { addDays } from '../lib/dates';
import { validateEvidenceLevel } from '../lib/evidence';
import { orgMarkets } from '../lib/orgPlan';
import {
  buildPostMortemFromGate45,
  findPostMortemForSprint,
  isPostMortemComplete,
} from '../lib/sprintPostMortem';
import { sprintStartOf } from '../lib/sprint';

/* ------------------------------------------------- Strategy Exception ---- */

export function ExceptionForm({ onClose, onSubmitted, prefill }) {
  const { add, today, state, d } = useStore();
  const trigger = EXCEPTION_TRIGGERS.includes(prefill?.trigger) ? prefill.trigger : EXCEPTION_TRIGGERS[0];
  const [f, setF] = useState({
    trigger,
    evidence: prefill?.evidence || prefill?.whyNow || '',
    upside: prefill?.upside || '',
    costCash: 0,
    costHours: 0,
    costProduct: '',
    costLegal: '',
    whatStops: prefill?.whatStops || '',
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const complete =
    String(f.evidence).trim().length > 30 && String(f.upside).trim() && String(f.whatStops).trim();

  const submit = () => {
    add('exceptions', {
      id: uid('exc'),
      date: today,
      ...f,
      decision: 'Pending',
      decidedAt: '',
      rationale: '',
    });
    onSubmitted?.();
    onClose();
  };

  const lockedRange = d?.sprint?.rangeLabel || 'this sprint';

  return (
    <Modal
      title="Request Strategy Exception"
      sub={`The strategy is locked ${lockedRange}. This is the only door.`}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={!complete}>
            Submit for decision
          </button>
        </>
      }
    >
      <Banner tone="stop">
        <strong>Curiosity about a parked market is not a trigger.</strong> An exception requires a
        specific external event, a named cost, and something you will stop doing — or a
        Wrong-tenant / Plan recompile trigger when the template itself is contaminated.
      </Banner>

      <div style={{ marginTop: 14 }}>
        <Select label="Trigger" value={f.trigger} onChange={set('trigger')} options={EXCEPTION_TRIGGERS} />
      </div>

      <Area
        label="Evidence — what actually happened?"
        value={f.evidence}
        onChange={set('evidence')}
        placeholder="Name the counterparty, the date, and what they did. Not what you felt."
        rows={4}
        help={
          String(f.evidence).trim().length <= 30
            ? 'Too thin. Describe the external event concretely.'
            : undefined
        }
      />

      <Area
        label="Expected upside — what could this change produce?"
        value={f.upside}
        onChange={set('upside')}
        placeholder="The specific evidence or cash this would create inside the 45 days."
      />

      <div className="section-head" style={{ marginTop: 6 }}><h3>Cost</h3></div>
      <div className="grid g2">
        <Money label="Cash" currency={state.settings?.currency} value={f.costCash} onChange={set('costCash')} />
        <Text label="Founder hours" type="number" value={f.costHours} onChange={(v) => set('costHours')(Number(v) || 0)} />
      </div>
      <div className="grid g2">
        <Text label="Product work" value={f.costProduct} onChange={set('costProduct')} placeholder="What must be built or changed?" />
        <Text label="Legal work" value={f.costLegal} onChange={set('costLegal')} placeholder="What must be reviewed or papered?" />
      </div>

      <Area
        label="What stops?"
        value={f.whatStops}
        onChange={set('whatStops')}
        placeholder="Name the current work that will be deprioritised. Founder time is fixed."
        help="An exception with no stop is an addition, and additions are how sprints die."
      />
    </Modal>
  );
}

/* -------------------------------------------------------- Daily closeout - */

export function CloseoutForm({ onClose }) {
  const { add, today, d } = useStore();
  const [f, setF] = useState({
    q1: '',
    q2: '',
    q3: '',
    q4: '',
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const submit = () => {
    add('closeouts', { id: uid('co'), date: today, q1: f.q1, q2: f.q2, q3: f.q3, q4: f.q4 });
    onClose();
  };

  const openCount = d.top3?.open?.length || 0;

  return (
    <Modal
      title="Daily closeout"
      sub="Four questions. Open actions stay in the backlog — roll-forward is a recommendation, not a new task."
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={submit}>Save closeout</button>
        </>
      }
    >
      <Area label="1. What evidence was created?" value={f.q1} onChange={set('q1')} rows={2} placeholder="Named counterparty and what they did. If nothing, write nothing." />
      <Area label="2. Which actions actually changed the state of the company?" value={f.q2} onChange={set('q2')} rows={2} />
      <Area label="3. What should roll forward?" value={f.q3} onChange={set('q3')} rows={2} placeholder="Name the open action that still matters. Do not invent a new one." />
      <Area label="4. Did today’s work reveal a new constraint?" value={f.q4} onChange={set('q4')} rows={2} placeholder="If yes, name it. Tomorrow’s briefing will start here." />
      <p className="dim tiny">{openCount} open action(s) already persist overnight. Closeout does not create tomorrow’s Top 3.</p>
    </Modal>
  );
}

/* ------------------------------------------------------- Product request - */

export function ProductRequestForm({ onClose }) {
  const { add, today } = useStore();
  const [f, setF] = useState({
    request: '',
    customer: '',
    opportunityId: '',
    blocker: '',
    commercialImpact: '',
    deadline: '',
    estimate: '',
    reuse: '',
    classification: 'FROZEN',
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const hasOpp = !!String(f.opportunityId).trim();
  const classification = hasOpp ? f.classification === 'FROZEN' ? 'CUSTOMER' : f.classification : 'FROZEN';

  const submit = () => {
    add('productRequests', { id: uid('pr'), date: today, ...f, classification });
    onClose();
  };

  return (
    <Modal
      title="Product request"
      sub="Product work must be attached to commercial proof."
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={!String(f.request).trim()}>Record request</button>
        </>
      }
    >
      <Text label="Request" value={f.request} onChange={set('request')} placeholder="What is being asked for?" />
      <div className="grid g2">
        <Text label="Customer / partner" value={f.customer} onChange={set('customer')} />
        <Text label="Opportunity ID" value={f.opportunityId} onChange={set('opportunityId')} placeholder="D001, INF-003 …" />
      </div>
      <Area label="What does it unblock?" value={f.blocker} onChange={set('blocker')} rows={2} />
      <div className="grid g2">
        <Text label="Commercial impact" value={f.commercialImpact} onChange={set('commercialImpact')} placeholder="Contract value or the deal it saves" />
        <Text label="Deadline" type="date" value={f.deadline} onChange={set('deadline')} />
      </div>
      <div className="grid g2">
        <Text label="Estimated work" value={f.estimate} onChange={set('estimate')} placeholder="Days" />
        <Text label="Reuse potential" value={f.reuse} onChange={set('reuse')} placeholder="Who else needs this?" />
      </div>
      <Select
        label="Classification"
        value={classification}
        onChange={set('classification')}
        options={PRODUCT_CLASSES}
        help={hasOpp ? undefined : 'No opportunity ID — this is FROZEN and cannot be reclassified.'}
      />
      {!hasOpp && <Banner tone="stop"><strong>FROZEN.</strong> This work is not currently attached to commercial proof.</Banner>}
    </Modal>
  );
}

/* ----------------------------------------------------------- Decision ---- */

export function DecisionForm({ onClose, defaults = {} }) {
  const { add, set, today } = useStore();
  const [f, setF] = useState({
    type: defaults.type || 'Strategy',
    question: defaults.question || '',
    options: defaults.options || '',
    evidence: defaults.evidence || '',
    decision: defaults.decision || '',
    reviewDate: defaults.reviewDate || addDays(today, 14),
  });
  const setField = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const submit = () => {
    add('decisions', { id: uid('dec'), date: today, ...f });
    // Strategy / supersede decisions bump lock chip (live P0-2 path).
    if (['Strategy', 'Sprint Initialization', 'Sprint re-baseline'].includes(f.type)) {
      set('settings.strategyApprovedAt', String(today).slice(0, 10));
    }
    onClose();
  };

  return (
    <Modal
      title="Log a decision"
      sub="Institutional memory. Future-you will not remember why."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={!String(f.question).trim() || !String(f.decision).trim()}>
            Log decision
          </button>
        </>
      }
    >
      <Select label="Type" value={f.type} onChange={setField('type')} options={DECISION_TYPES} />
      <Text label="Question" value={f.question} onChange={setField('question')} placeholder="What was actually being decided?" />
      <Area label="Options considered" value={f.options} onChange={setField('options')} rows={2} />
      <Area label="Evidence relied on" value={f.evidence} onChange={setField('evidence')} rows={2} placeholder="What did the market show? Not what you hoped." />
      <Area label="Decision" value={f.decision} onChange={setField('decision')} rows={2} />
      <Text label="Review date" type="date" value={f.reviewDate} onChange={setField('reviewDate')} />
    </Modal>
  );
}

/* ---------------------------------------------------- Constraint editor -- */

export function ConstraintForm({ onClose, current, onSave, title = 'Set the constraint' }) {
  const [f, setF] = useState({
    blocker: current?.blocker || '',
    owner: current?.owner || 'Founder',
    due: current?.due || '',
    evidenceNeeded: current?.evidenceNeeded || '',
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  return (
    <Modal
      title={title}
      sub="Only one thing can be the primary constraint."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={() => { onSave(f); onClose(); }} disabled={!String(f.blocker).trim()}>
            Save constraint
          </button>
        </>
      }
    >
      <Area label="Blocker" value={f.blocker} onChange={set('blocker')} rows={2} placeholder="The single thing most limiting company value right now." />
      <div className="grid g2">
        <Text label="Owner" value={f.owner} onChange={set('owner')} />
        <Text label="Due" type="date" value={f.due} onChange={set('due')} />
      </div>
      <Area label="Evidence needed to close it" value={f.evidenceNeeded} onChange={set('evidenceNeeded')} rows={2} placeholder="e.g. E4 — licensed partner accepts the D001 architecture in writing." />
    </Modal>
  );
}

/* ------------------------------------------------------------- Expense --- */

export function ExpenseForm({ onClose, defaults = {} }) {
  const { add, today, state } = useStore();
  const [f, setF] = useState({
    date: defaults.date || today,
    label: '',
    amount: 0,
    category: 'KEEP',
    linkedTo: '',
    evidenceObjective: '',
    reviewDate: addDays(today, 30),
    recurring: 'one-off',
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const material = Number(f.amount) >= (state.settings?.alertThresholds?.materialSpend ?? 5000);

  return (
    <Modal
      title="Record spend"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={() => { add('expenses', { id: uid('exp'), ...f }); onClose(); }} disabled={!String(f.label).trim()}>
            Record
          </button>
        </>
      }
    >
      <div className="grid g2">
        <Text label="Item" value={f.label} onChange={set('label')} />
        <Money label="Amount" currency={state.settings?.currency} value={f.amount} onChange={set('amount')} />
      </div>
      <div className="grid g2">
        <Select label="Category" value={f.category} onChange={set('category')} options={['KEEP', 'PROVE', 'EXTEND', 'STOP']} />
        <Select label="Recurring" value={f.recurring} onChange={set('recurring')} options={['one-off', 'monthly']} />
      </div>
      <div className="grid g2">
        <Text label="Opportunity / deal" value={f.linkedTo} onChange={set('linkedTo')} placeholder="D001, INF-002 …" />
        <Text label="Review date" type="date" value={f.reviewDate} onChange={set('reviewDate')} />
      </div>
      <Area
        label="Evidence objective"
        value={f.evidenceObjective}
        onChange={set('evidenceObjective')}
        rows={2}
        placeholder="What evidence is this money buying?"
      />
      {material && !String(f.evidenceObjective).trim() && (
        <Banner tone="stop">This is material spend with no evidence objective. It will be flagged on Command.</Banner>
      )}
      <div className="grid g2">
        <Text label="Date" type="date" value={f.date} onChange={set('date')} />
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------ Quick log entry -- */

export function QuickLog({ onClose }) {
  const { state, add, today } = useStore();
  const markets = orgMarkets(state).map((m) => m.label);
  const [f, setF] = useState({ market: markets[0] || '', level: ACTIVITY.id, title: '', counterparty: '' });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const validation = validateEvidenceLevel({ ...f, date: today });
  return (
    <Modal
      title="Quick log"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn primary"
            disabled={!String(f.title).trim()}
            onClick={() => {
              add('evidence', {
                id: uid('ev'),
                date: today,
                ...f,
                level: validation.level,
                validationOk: validation.ok,
                validationReason: validation.reason || undefined,
              });
              onClose();
            }}
          >
            Log{!validation.ok ? ` as ${validation.level}` : ''}
          </button>
        </>
      }
    >
      <Text label="What happened" value={f.title} onChange={set('title')} />
      <Text label="Counterparty" value={f.counterparty} onChange={set('counterparty')} placeholder="Named party if proof" />
      <div className="grid g2">
        <Select label="Market" value={f.market} onChange={set('market')} options={markets} />
        <Select
          label="Level"
          value={f.level}
          onChange={set('level')}
          options={[{ value: ACTIVITY.id, label: 'Activity' }, ...EVIDENCE_LEVELS.map((e) => ({ value: e.id, label: `${e.id} — ${e.name}` }))]}
        />
      </div>
      {!validation.ok && validation.reason ? (
        <Banner tone="warn">{validation.reason}</Banner>
      ) : null}
      <div className="row tight">
        <span className="dim tiny">Recording as</span>
        <EvidenceBadge level={validation.level} showName />
      </div>
    </Modal>
  );
}

const GATE_OPTIONS = ['A', 'B', 'C', 'D', 'E'];

/**
 * Sprint post-mortem — Day 45 close or early cycle-close before rebaseline.
 * Idempotent per sprintStart (patch existing or add).
 */
export function SprintPostMortemForm({ onClose, onSaved, earlyClose = false, endedBy }) {
  const { state, add, patch, today } = useStore();
  const by = endedBy || (earlyClose ? 'rebaseline' : 'day45');
  const seed = buildPostMortemFromGate45(state, { today, endedBy: by, earlyClose });
  const [f, setF] = useState(() => ({
    ...seed,
    runAgain: seed.runAgain === true ? 'yes' : seed.runAgain === false ? 'no' : '',
  }));
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const draft = {
    ...f,
    earlyClose: !!earlyClose,
    endedBy: by,
    endedAt: today,
    runAgain: f.runAgain === 'yes' ? true : f.runAgain === 'no' ? false : undefined,
  };
  const complete = isPostMortemComplete(draft);

  const submit = () => {
    if (!complete) return;
    const existing = findPostMortemForSprint(state, draft.sprintStart);
    const id = existing?.id || draft.id || uid('spr');
    const decisionId = uid('dec');
    const row = {
      ...draft,
      id,
      decisionId,
      runAgain: draft.runAgain,
    };
    if (existing) patch('sprintReviews', existing.id, row);
    else add('sprintReviews', row);

    add('decisions', {
      id: decisionId,
      date: today,
      type: 'Sprint closeout',
      question: earlyClose
        ? `Early cycle-close for sprint starting ${draft.sprintStart}?`
        : `Day-45 company decision for sprint starting ${draft.sprintStart}?`,
      options: draft.option ? `Option ${draft.option}` : 'Early close — no Day-45 option',
      evidence: draft.proved,
      decision: draft.carryForward,
      reviewDate: today,
      postMortemId: id,
    });
    onSaved?.(row);
    onClose();
  };

  const t0 = sprintStartOf(state);

  return (
    <Modal
      title={earlyClose ? 'Early cycle-close post-mortem' : 'Sprint post-mortem'}
      sub={
        earlyClose
          ? `Required before re-baselining. Ending T0 ${t0}. Does not invent a Day-45 gate.`
          : `Archive lessons for T0 ${t0}. Required before starting the next sprint.`
      }
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={!complete}>
            Save post-mortem
          </button>
        </>
      }
    >
      {!earlyClose && !String(f.option || '').trim() && (
        <Banner tone="stop">Choose an operating-model option (A–E) on Day 45 before saving, or pick it here.</Banner>
      )}
      {earlyClose && (
        <Banner>Early close: gate option is optional. Carry-forward and what you proved still are not.</Banner>
      )}

      {!earlyClose && (
        <Select
          label="Gate outcome (A–E)"
          value={f.option || ''}
          onChange={set('option')}
          options={[{ value: '', label: '—' }, ...GATE_OPTIONS.map((k) => ({ value: k, label: k }))]}
        />
      )}

      <Area
        label="What we proved or killed"
        value={f.proved}
        onChange={set('proved')}
        rows={3}
        placeholder="Only things a counterparty did — or what the market killed."
      />
      <Area
        label="Biggest miss or surprise"
        value={f.biggestMiss}
        onChange={set('biggestMiss')}
        rows={2}
        placeholder="One blunt line."
      />
      <Area
        label="Why that decision"
        value={f.rationale}
        onChange={set('rationale')}
        rows={2}
        placeholder="Why this option / early close — not the alternatives."
      />
      <Area
        label="Carry-forward for the next T0"
        value={f.carryForward}
        onChange={set('carryForward')}
        rows={2}
        placeholder="The single constraint or focus the next sprint inherits."
      />
      <div className="grid g2">
        <Select
          label="Would we run this sprint design again?"
          value={f.runAgain}
          onChange={set('runAgain')}
          options={[
            { value: '', label: '—' },
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
        />
        <Text label="Why" value={f.runAgainWhy} onChange={set('runAgainWhy')} placeholder="Short honest note" />
      </div>
      {(f.disproved || f.stop || f.concentrate) && (
        <div className="dim tiny" style={{ marginTop: 8 }}>
          From Day 45: stop — {f.stop || '—'} · concentrate — {f.concentrate || '—'}
        </div>
      )}
    </Modal>
  );
}

